import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface LiveMatch {
  id: string;
  match_id: string;
  match_type: 'division' | 'world_cup' | 'tournament' | 'external';
  status: 'live' | 'paused' | 'ended';
  current_minute: number;
  current_period: string;
  score_home: number;
  score_away: number;
  is_clock_running: boolean;
  clock_started_at: string | null;
  accumulated_seconds: number;
  started_at: string;
  paused_at: string | null;
  ended_at: string | null;
  created_by: string;
  updated_at: string;
}

export interface LiveMatchEvent {
  id: string;
  live_match_id: string;
  event_type: string;
  minute: number;
  added_time: number;
  team: 'home' | 'away' | null;
  player_id: string | null;
  player_name: string | null;
  secondary_player_id: string | null;
  secondary_player_name: string | null;
  details: Record<string, unknown>;
  is_deleted: boolean;
  created_at: string;
}

// Hook to get current calculated minute from clock
export const useCalculatedMinute = (liveMatch: LiveMatch | null): number => {
  const [currentMinute, setCurrentMinute] = useState(0);

  useEffect(() => {
    if (!liveMatch) {
      setCurrentMinute(0);
      return;
    }

    const calculateMinute = () => {
      let elapsed = liveMatch.accumulated_seconds;
      
      if (liveMatch.is_clock_running && liveMatch.clock_started_at) {
        const startTime = new Date(liveMatch.clock_started_at).getTime();
        const now = Date.now();
        const additionalSeconds = Math.floor((now - startTime) / 1000);
        elapsed += additionalSeconds;
      }
      
      return Math.floor(elapsed / 60);
    };

    setCurrentMinute(calculateMinute());

    // Update every second if clock is running
    if (liveMatch.is_clock_running) {
      const interval = setInterval(() => {
        setCurrentMinute(calculateMinute());
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [liveMatch]);

  return currentMinute;
};

// Hook to subscribe to a specific live match
export const useLiveMatchSubscription = (liveMatchId: string | null) => {
  const [liveMatch, setLiveMatch] = useState<LiveMatch | null>(null);
  const [events, setEvents] = useState<LiveMatchEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch initial data
  useEffect(() => {
    if (!liveMatchId) {
      setLiveMatch(null);
      setEvents([]);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch live match
        const { data: matchData, error: matchError } = await supabase
          .from('live_matches')
          .select('*')
          .eq('id', liveMatchId)
          .single();

        if (matchError) throw matchError;
        setLiveMatch(matchData as LiveMatch);

        // Fetch events
        const { data: eventsData, error: eventsError } = await supabase
          .from('live_match_events')
          .select('*')
          .eq('live_match_id', liveMatchId)
          .eq('is_deleted', false)
          .order('minute', { ascending: true })
          .order('created_at', { ascending: true });

        if (eventsError) throw eventsError;
        setEvents(eventsData as LiveMatchEvent[]);
      } catch (err) {
        console.error('Error fetching live match:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch live match');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [liveMatchId]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!liveMatchId) return;

    let matchChannel: RealtimeChannel;
    let eventsChannel: RealtimeChannel;

    // Subscribe to live match updates
    matchChannel = supabase
      .channel(`live-match-${liveMatchId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_matches',
          filter: `id=eq.${liveMatchId}`,
        },
        (payload) => {
          console.log('Live match update:', payload);
          if (payload.eventType === 'UPDATE') {
            setLiveMatch(payload.new as LiveMatch);
          } else if (payload.eventType === 'DELETE') {
            setLiveMatch(null);
          }
        }
      )
      .subscribe();

    // Subscribe to live match events
    eventsChannel = supabase
      .channel(`live-match-events-${liveMatchId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_match_events',
          filter: `live_match_id=eq.${liveMatchId}`,
        },
        (payload) => {
          console.log('Live match event update:', payload);
          if (payload.eventType === 'INSERT') {
            const newEvent = payload.new as LiveMatchEvent;
            if (!newEvent.is_deleted) {
              setEvents((prev) => [...prev, newEvent].sort((a, b) => {
                if (a.minute !== b.minute) return a.minute - b.minute;
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
              }));
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedEvent = payload.new as LiveMatchEvent;
            if (updatedEvent.is_deleted) {
              setEvents((prev) => prev.filter((e) => e.id !== updatedEvent.id));
            } else {
              setEvents((prev) =>
                prev.map((e) => (e.id === updatedEvent.id ? updatedEvent : e))
              );
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(matchChannel);
      supabase.removeChannel(eventsChannel);
    };
  }, [liveMatchId]);

  return { liveMatch, events, isLoading, error };
};

// Hook to get all active live matches
export const useActiveLiveMatches = () => {
  const [liveMatches, setLiveMatches] = useState<LiveMatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLiveMatches = async () => {
      const { data, error } = await supabase
        .from('live_matches')
        .select('*')
        .in('status', ['live', 'paused'])
        .order('started_at', { ascending: false });

      if (!error && data) {
        setLiveMatches(data as LiveMatch[]);
      }
      setIsLoading(false);
    };

    fetchLiveMatches();

    // Subscribe to all live match changes
    const channel = supabase
      .channel('all-live-matches')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_matches',
        },
        (payload) => {
          console.log('Live matches list update:', payload);
          if (payload.eventType === 'INSERT') {
            const newMatch = payload.new as LiveMatch;
            if (newMatch.status !== 'ended') {
              setLiveMatches((prev) => [newMatch, ...prev]);
            }
          } else if (payload.eventType === 'UPDATE') {
            const updated = payload.new as LiveMatch;
            if (updated.status === 'ended') {
              setLiveMatches((prev) => prev.filter((m) => m.id !== updated.id));
            } else {
              setLiveMatches((prev) =>
                prev.map((m) => (m.id === updated.id ? updated : m))
              );
            }
          } else if (payload.eventType === 'DELETE') {
            setLiveMatches((prev) => prev.filter((m) => m.id !== (payload.old as LiveMatch).id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { liveMatches, isLoading };
};

// Hook for finding live match by original match ID
export const useLiveMatchByMatchId = (matchId: string | null, matchType: string) => {
  const [liveMatch, setLiveMatch] = useState<LiveMatch | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!matchId) {
      setLiveMatch(null);
      setIsLoading(false);
      return;
    }

    const fetchLiveMatch = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('live_matches')
        .select('*')
        .eq('match_id', matchId)
        .eq('match_type', matchType)
        .in('status', ['live', 'paused'])
        .maybeSingle();

      if (!error) {
        setLiveMatch(data as LiveMatch | null);
      }
      setIsLoading(false);
    };

    fetchLiveMatch();

    // Subscribe to changes for this specific match
    const channel = supabase
      .channel(`live-match-by-id-${matchId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_matches',
          filter: `match_id=eq.${matchId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const updated = payload.new as LiveMatch;
            if (updated.status === 'ended') {
              setLiveMatch(null);
            } else {
              setLiveMatch(updated);
            }
          } else if (payload.eventType === 'DELETE') {
            setLiveMatch(null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [matchId, matchType]);

  return { liveMatch, isLoading };
};
